import { Component, Inject, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PrestamosService } from '../../../../../modules/finanzas/prestamos/prestamos.service';
import {
  ConstitucionPrestamosModel,
  DeudaACortoPlazoPrestamosModel,
  PagoCuotasPrestamosModel
} from '../../../../../modules/finanzas/prestamos/prestamos.model';
import { ActivityService } from '../../../../../modules/user/activity/activity.service';
import { MasterService } from '../../../../master/services/master.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { ActualSelected, Utils } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { currentActivityOption, EntityFormOption, findOption, InjectedOptions, optionId } from '../../../entity-form/entity-form.utils';
import { LoanOperationDialogData, LoanOperationKind, roundMoney, stripLoanConcept } from '../../models/loan.model';
import { LoanFormLookupsFacade } from '../../services/loan-form-lookups.facade';

type LoanOperationItem = ConstitucionPrestamosModel & DeudaACortoPlazoPrestamosModel & PagoCuotasPrestamosModel & {
  bankAccountInfo?: string;
  activitiesName?: string;
};

@Component({
  selector: 'app-loan-operation-dialog',
  templateUrl: './loan-operation-dialog.component.html',
  styleUrls: ['./loan-operation-dialog.component.scss'],
  standalone: false,
})
export class LoanOperationDialogComponent implements OnInit {
  ready = false;
  loadError = false;
  saving = false;
  savingMessage = '';
  saveError = '';
  submitted = false;
  changed = false;

  items: LoanOperationItem[] = [];
  editing: LoanOperationItem | null = null;

  form: UntypedFormGroup;

  activities: EntityFormOption[] = [];
  bankAccounts: EntityFormOption[] = [];
  projects: EntityFormOption[] = [];
  expenseProjectTypes: EntityFormOption[] = [];
  isProjectEnable = false;

  pendingAmount = 0;

  readonly year = ActualSelected.yearSelected;

  private model: any = {};

  private readonly injected = new InjectedOptions();
  private requestedEditDone = false;

  static open(dialog: MatDialog, data: LoanOperationDialogData): Observable<boolean> {
    return dialog.open<LoanOperationDialogComponent, LoanOperationDialogData, boolean>(LoanOperationDialogComponent, {
      width: '680px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<LoanOperationDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: LoanOperationDialogData,
    private prestamosService: PrestamosService,
    private activityService: ActivityService,
    private masterService: MasterService,
    private lookups: LoanFormLookupsFacade,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) {
    this.form = this.fb.group({
      fecha: [null],
      concepto: [''],
      importe: [null],
      importeCapital: [null],
      comisionApertura: [null],
      intereses: [null],
      gastosBancarios: [null],
      activity: [null],
      bankAccount: [null],
      project: [null],
      expenseProjectType: [null],
    });
  }

  get kind(): LoanOperationKind {
    return this.data.kind;
  }

  get isConstitution(): boolean { return this.kind === 'constitution'; }
  get isShortTermDebt(): boolean { return this.kind === 'shortTermDebt'; }
  get isInstallment(): boolean { return this.kind === 'installment'; }

  get needsBankAccount(): boolean {
    return !this.isShortTermDebt;
  }

  get showProjects(): boolean {
    return !this.isShortTermDebt && this.isProjectEnable;
  }

  get title(): string {
    if (this.isConstitution) return this.data.loan.duracion === 1 ? 'Constitución del préstamo a corto plazo' : this.data.loan.duracion === 2 ? 'Constitución del préstamo a largo plazo' : 'Constitución del préstamo';
    if (this.isShortTermDebt) return 'Reclasificar deuda a corto plazo';
    return 'Pago de cuotas';
  }

  get subtitle(): string {
    return [this.data.loan.entidad, this.data.loan.numero ? 'Nº ' + this.data.loan.numero : null].filter(Boolean).join(' · ');
  }

  get icon(): string {
    if (this.isConstitution) return 'account_balance';
    if (this.isShortTermDebt) return 'swap_horiz';
    return 'payment';
  }

  get amountLabel(): string {
    return this.isInstallment ? 'Importe capital' : 'Importe';
  }

  get amountControl(): UntypedFormControl {
    return this.control(this.isInstallment ? 'importeCapital' : 'importe');
  }

  get listTitle(): string {
    if (this.isConstitution) return 'Constituciones del ejercicio ' + this.year;
    if (this.isShortTermDebt) return 'Reclasificaciones del ejercicio ' + this.year;
    return 'Cuotas pagadas en el ejercicio ' + this.year;
  }

  get emptyText(): string {
    if (this.isConstitution) return 'No hay constituciones registradas en el ejercicio.';
    if (this.isShortTermDebt) return 'No hay deuda reclasificada a corto plazo en el ejercicio.';
    return 'No hay cuotas pagadas en el ejercicio.';
  }

  get saveText(): string {
    if (this.editing) return 'Guardar cambios';
    if (this.isConstitution) return 'Añadir constitución';
    if (this.isShortTermDebt) return 'Reclasificar';
    return 'Pagar';
  }

  get paidCapital(): number {
    return roundMoney(this.items.reduce((acc, item) => acc + (Number(item.importeCapital) || 0), 0));
  }

  get paidInterest(): number {
    return roundMoney(this.items.reduce((acc, item) => acc + (Number(item.intereses) || 0), 0));
  }

  get itemsTotal(): number {
    return roundMoney(this.items.reduce((acc, item) => acc + (Number(item.importe) || 0), 0));
  }

  get pendingAfterPayment(): number {
    return roundMoney(this.pendingAmount - (Number(this.form.get('importeCapital').value) || 0));
  }

  get missingDate(): boolean { return !this.form.get('fecha').value; }
  get missingConcept(): boolean { return !String(this.form.get('concepto').value || '').trim(); }
  get invalidAmount(): boolean { return !Number(this.amountControl.value); }
  get missingActivity(): boolean { return optionId(this.form.get('activity').value) === null; }
  get missingBankAccount(): boolean {
    return this.needsBankAccount && optionId(this.form.get('bankAccount').value) === null;
  }

  get hasErrors(): boolean {
    return this.missingDate || this.missingConcept || this.invalidAmount || this.missingActivity || this.missingBankAccount;
  }

  control(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  ngOnInit(): void {
    this.pendingAmount = roundMoney(this.data.loan.importePendiente);
    this.resetForm();
    this.load();
  }

  retry(): void {
    this.loadError = false;
    this.load();
  }

  startEdit(item: LoanOperationItem): void {
    if (this.saving) return;
    this.editing = item;
    this.submitted = false;
    this.saveError = '';
    this.injected.clear();
    this.model = { ...item };
    this.form.patchValue({
      fecha: item.fecha || null,
      concepto: this.itemConcept(item),
      importe: item.importe != null ? Number(item.importe) : null,
      importeCapital: item.importeCapital != null ? Number(item.importeCapital) : null,
      comisionApertura: item.comisionApertura != null ? Number(item.comisionApertura) : null,
      intereses: item.intereses != null ? Number(item.intereses) : null,
      gastosBancarios: item.gastosBancarios != null ? Number(item.gastosBancarios) : null,
      activity: this.injected.ensure(this.activities, item.activitiesUid, item.activitiesName),
      bankAccount: this.injected.ensure(this.bankAccounts, (item as any).bankAccountsUid, item.bankAccountInfo),
      project: this.injected.ensure(this.projects, (item as any).projectUid, (item as any).projectName),
      expenseProjectType: this.injected.ensure(this.expenseProjectTypes, (item as any).expenseProjectTypeUid, (item as any).expenseProjectTypeName),
    }, { emitEvent: false });
    this.applySingleOptionDefaults();
  }

  cancelEdit(): void {
    this.editing = null;
    this.submitted = false;
    this.saveError = '';
    this.resetForm();
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';
    if (this.hasErrors) return;

    const payload = this.buildPayload();
    const editing = !!this.editing;
    this.saving = true;
    this.savingMessage = editing ? 'Guardando cambios…' : this.isInstallment ? 'Registrando pago…' : this.isConstitution ? 'Registrando constitución…' : 'Reclasificando deuda…';
    this.dialogRef.disableClose = true;

    this.request(payload, editing)
      .then(ok => {
        if (ok !== true) throw new Error();
        this.changed = true;
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.notify.ok(this.successMessage(editing));
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        const serverMessage = error && error.error && error.error.message;
        this.saveError = serverMessage || 'No se pudo guardar. Revisa los datos e inténtalo de nuevo.';
      });
  }

  remove(item: LoanOperationItem): void {
    if (this.saving) return;
    this.confirm.open({
      title: this.isConstitution ? 'Eliminar constitución' : this.isShortTermDebt ? 'Eliminar reclasificación' : 'Eliminar pago de cuota',
      subtitle: this.subtitle || undefined,
      message: this.deleteMessage(),
      tone: 'danger',
      details: this.itemDetails(item),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando…',
      errorText: 'No se pudo eliminar el movimiento',
      action: () => this.deleteRequest(item.uid).then(ok => { if (ok !== true) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changed = true;
      if (this.isInstallment) {
        this.pendingAmount = roundMoney(this.pendingAmount + (Number(item.importeCapital) || 0));
      }
      this.notify.ok('Movimiento eliminado correctamente');
      if (this.editing && this.editing.uid === item.uid) this.cancelEdit();
      this.loadItems(false);
    });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changed);
  }

  trackByUid(_: number, item: LoanOperationItem): string {
    return item.uid;
  }

  itemAmount(item: LoanOperationItem): number {
    return this.isInstallment ? Number(item.importeCapital) || 0 : Number(item.importe) || 0;
  }

  itemMeta(item: LoanOperationItem): string {
    const parts: string[] = [this.itemConcept(item)];
    if (this.isConstitution && item.comisionApertura) parts.push('Comisión ' + Utils.FormatMoney(item.comisionApertura) + ' €');
    if (this.isInstallment && item.intereses) parts.push('Intereses ' + Utils.FormatMoney(item.intereses) + ' €');
    if (!this.isShortTermDebt && item.gastosBancarios) parts.push('Gastos ' + Utils.FormatMoney(item.gastosBancarios) + ' €');
    if (item.bankAccountInfo) parts.push(item.bankAccountInfo);
    return parts.filter(Boolean).join(' · ');
  }

  itemConcept(item: LoanOperationItem): string {
    return this.isShortTermDebt ? (item.concepto || '') : stripLoanConcept(item.concepto, this.data.loan);
  }


  private resetForm(): void {
    this.injected.clear();
    const loan = this.data.loan;
    this.model = this.newModel();
    const bankAccountUid = loan.bankAccountsUid && loan.bankAccountsUid !== consts.EMPTY_GUID ? loan.bankAccountsUid : null;
    this.form.reset({
      fecha: this.isConstitution ? loan.fechaConstitucion : null,
      concepto: '',
      importe: this.isConstitution && loan.importeInicial != null ? Number(loan.importeInicial) : null,
      importeCapital: null,
      comisionApertura: this.isConstitution && loan.comisionApertura != null ? Number(loan.comisionApertura) : null,
      intereses: null,
      gastosBancarios: null,
      activity: null,
      bankAccount: this.needsBankAccount ? findOption(this.bankAccounts, bankAccountUid) : null,
      project: null,
      expenseProjectType: null,
    }, { emitEvent: false });
    this.applySingleOptionDefaults();
  }

  private newModel(): any {
    if (this.isConstitution) return new ConstitucionPrestamosModel();
    if (this.isShortTermDebt) return new DeudaACortoPlazoPrestamosModel();
    return new PagoCuotasPrestamosModel();
  }

  private buildPayload(): any {
    const value = this.form.getRawValue();
    const payload: any = { ...this.model, prestamosId: this.data.loan.uid };
    payload.fecha = value.fecha;
    payload.concepto = value.concepto;
    payload.activitiesUid = optionId(value.activity);

    if (this.isInstallment) {
      payload.importeCapital = Number(value.importeCapital) || 0;
      payload.intereses = Number(value.intereses) || 0;
    } else {
      payload.importe = Number(value.importe) || 0;
    }
    if (this.isConstitution) {
      payload.comisionApertura = Number(value.comisionApertura) || 0;
    }
    if (!this.isShortTermDebt) {
      payload.gastosBancarios = Number(value.gastosBancarios) || 0;
      payload.bankAccountsUid = optionId(value.bankAccount);
    }
    if (this.showProjects) {
      payload.projectUid = optionId(value.project);
      payload.expenseProjectTypeUid = optionId(value.expenseProjectType);
    }
    return payload;
  }

  private request(payload: any, editing: boolean): Promise<boolean> {
    if (this.isConstitution) {
      return editing ? this.prestamosService.constitucionUpdate(payload) : this.prestamosService.constitucionCreate(payload);
    }
    if (this.isShortTermDebt) {
      return editing ? this.prestamosService.deudaACortoPlazoUpdate(payload) : this.prestamosService.deudaACortoPlazoCreate(payload);
    }
    return editing ? this.prestamosService.pagoCoutasUpdate(payload) : this.prestamosService.pagoCoutasCreate(payload);
  }

  private deleteRequest(uid: string): Promise<boolean> {
    if (this.isConstitution) return this.prestamosService.constitucionDelete(uid);
    if (this.isShortTermDebt) return this.prestamosService.deudaACortoPlazoDelete(uid);
    return this.prestamosService.pagoCoutasDelete(uid);
  }

  private listRequest(): Promise<LoanOperationItem[]> {
    const uid = this.data.loan.uid;
    if (this.isConstitution) return this.prestamosService.constitucionList(uid) as Promise<LoanOperationItem[]>;
    if (this.isShortTermDebt) return this.prestamosService.deudaACortoPlazoList(uid) as Promise<LoanOperationItem[]>;
    return this.prestamosService.pagoCoutasList(uid) as Promise<LoanOperationItem[]>;
  }

  private load(): void {
    this.ready = false;
    Promise.all([
      this.listRequest(),
      this.loadActivities(),
      this.loadBankAccounts(),
      this.loadProjects(),
    ])
      .then(([items, activities, bankAccounts, projectLookups]) => {
        this.activities = activities;
        this.bankAccounts = bankAccounts;
        this.isProjectEnable = projectLookups.enabled;
        this.projects = projectLookups.projects;
        this.expenseProjectTypes = projectLookups.expenseProjectTypes;
        this.items = Array.isArray(items) ? items : [];
        this.resetForm();
        this.openRequestedEdit();
      })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  private loadActivities(): Promise<EntityFormOption[]> {
    return this.activityService.getList()
      .then(list => (list || [])
        .filter(activity => activity && activity.uid && activity.uid !== consts.EMPTY_GUID)
        .map(activity => ({ id: activity.uid, display: activity.name || '' })))
      .catch(() => [] as EntityFormOption[]);
  }

  private loadBankAccounts(): Promise<EntityFormOption[]> {
    if (!this.needsBankAccount) return Promise.resolve([] as EntityFormOption[]);
    return this.lookups.bankAccounts().catch(() => [] as EntityFormOption[]);
  }

  private loadProjects(): Promise<{ enabled: boolean; projects: EntityFormOption[]; expenseProjectTypes: EntityFormOption[] }> {
    const empty = { enabled: false, projects: [] as EntityFormOption[], expenseProjectTypes: [] as EntityFormOption[] };
    if (this.isShortTermDebt) return Promise.resolve(empty);
    return this.activityService.loadData(ActualSelected.activitySelected)
      .then(activity => !!(activity && activity.isProject))
      .catch(() => false)
      .then(enabled => {
        if (!enabled) return empty;
        return Promise.all([
          firstValueFrom(this.masterService.getProjects()).catch(() => []),
          firstValueFrom(this.masterService.getExpenseProjectTypes()).catch(() => []),
        ]).then(([projectList, typeList]) => ({
          enabled,
          projects: (projectList || []).map(project => ({ id: project.id, display: project.display || '' })),
          expenseProjectTypes: (typeList || []).map(type => ({ id: type.id, display: type.display || '' })),
        }));
      });
  }

  private loadItems(initial: boolean): void {
    if (initial) this.ready = false;
    this.listRequest()
      .then(items => {
        this.items = Array.isArray(items) ? items : [];
        this.openRequestedEdit();
      })
      .catch(() => {
        if (initial) {
          this.loadError = true;
        } else {
          this.notify.error('No se pudieron recargar los movimientos');
        }
      })
      .then(() => { this.ready = true; });
  }

  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const item = this.items.find(i => i.uid === this.data.editUid);
    if (item) this.startEdit(item);
  }

  private applySingleOptionDefaults(): void {
    if (optionId(this.form.get('activity').value) === null) {
      const current = this.editing ? null : currentActivityOption(this.activities);
      if (current) this.control('activity').setValue(current, { emitEvent: false });
      else if (this.activities.length === 1) this.control('activity').setValue(this.activities[0], { emitEvent: false });
    }
    if (this.needsBankAccount && this.bankAccounts.length) {
      const current = optionId(this.form.get('bankAccount').value);
      if (current !== null && !findOption(this.bankAccounts, current)) {
        this.control('bankAccount').setValue(null, { emitEvent: false });
      }
      if (optionId(this.form.get('bankAccount').value) === null && this.bankAccounts.length === 1) {
        this.control('bankAccount').setValue(this.bankAccounts[0], { emitEvent: false });
      }
    }
  }

  private successMessage(editing: boolean): string {
    if (this.isConstitution) return editing ? 'Constitución actualizada correctamente' : 'Constitución añadida correctamente';
    if (this.isShortTermDebt) return editing ? 'Reclasificación actualizada correctamente' : 'Deuda reclasificada a corto plazo';
    return editing ? 'Pago actualizado correctamente' : 'Pago guardado correctamente';
  }

  private deleteMessage(): string {
    if (this.isConstitution) return 'Se eliminará la constitución y su asiento contable. Si estaba conciliada, el movimiento bancario volverá a quedar pendiente.';
    if (this.isShortTermDebt) return 'Se eliminará la reclasificación a corto plazo y su asiento contable.';
    return 'Se eliminará el pago y su asiento contable, y el capital volverá a sumarse al importe pendiente del préstamo. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente.';
  }

  private itemDetails(item: LoanOperationItem): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (item.fecha) details.push({ label: 'Fecha', value: formatDate(item.fecha, 'dd/MM/yyyy', 'es-ES') });
    const concept = this.itemConcept(item);
    if (concept) details.push({ label: 'Concepto', value: concept });
    details.push({ label: this.amountLabel, value: Utils.FormatMoney(this.itemAmount(item)) + ' €' });
    if (this.isInstallment) details.push({ label: 'Intereses', value: Utils.FormatMoney(Number(item.intereses) || 0) + ' €' });
    return details;
  }
}
